import axios from 'axios';

// Create axios instance
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8004',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('super_admin_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle token expiration
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('super_admin_token');
      window.location.href = '/superadmin/login';
    }
    return Promise.reject(error);
  }
);

// Super Admin API
export const superAdminAPI = {
  // Setup
  checkSetupStatus: async () => {
    const response = await api.get('/api/superadmin/setup/status');
    return response.data;
  },

  initializeSetup: async (setupData) => {
    const response = await api.post('/api/superadmin/setup/initialize', setupData);
    return response.data;
  },

  // Authentication
  login: async (credentials) => {
    const response = await api.post('/api/superadmin/auth/login', credentials);
    return response.data;
  },

  logout: async () => {
    const response = await api.post('/api/superadmin/auth/logout');
    return response.data;
  },

  verifyToken: async () => {
    const response = await api.get('/api/superadmin/auth/verify');
    return response.data;
  },

  // Customer Management
  getAllCustomers: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const response = await api.get(`/api/superadmin/customers${queryString ? '?' + queryString : ''}`);
    return response.data;
  },

  getCustomer: async (tenantId) => {
    const response = await api.get(`/api/superadmin/customers/${tenantId}`);
    return response.data;
  },

  updateCustomer: async (tenantId, updates) => {
    const response = await api.put(`/api/superadmin/customers/${tenantId}`, updates);
    return response.data;
  },

  suspendCustomer: async (tenantId) => {
    const response = await api.post(`/api/superadmin/customers/${tenantId}/suspend`);
    return response.data;
  },

  activateCustomer: async (tenantId) => {
    const response = await api.post(`/api/superadmin/customers/${tenantId}/activate`);
    return response.data;
  },

  setCustomerTrial: async (tenantId, { days, email, company_name, plan }) => {
    const body = { days };
    if (email) body.email = email;
    if (company_name) body.company_name = company_name;
    if (plan) body.plan = plan;
    const response = await api.post(`/api/superadmin/customers/${tenantId}/trial`, body);
    return response.data;
  },

  getCognitoUsers: async ({ only_missing_profile = false } = {}) => {
    const response = await api.get(`/api/superadmin/cognito-users`, {
      params: { only_missing_profile },
    });
    return response.data;
  },

  // Platform Analytics
  getPlatformStats: async () => {
    const response = await api.get('/api/superadmin/analytics/platform-stats');
    return response.data;
  },

  getRecentActivity: async (limit = 10) => {
    const response = await api.get(`/api/superadmin/analytics/recent-activity?limit=${limit}`);
    return response.data;
  },

  getUsageStats: async (period = '30d') => {
    const response = await api.get(`/api/super-admin/analytics/usage?period=${period}`);
    return response.data;
  },

  getRevenueStats: async (period = '30d') => {
    const response = await api.get(`/api/super-admin/analytics/revenue?period=${period}`);
    return response.data;
  },

  // System Health
  getSystemHealth: async () => {
    const response = await api.get('/api/superadmin/system/health');
    return response.data;
  },

  // Support & Billing
  getBillingData: async (tenantId) => {
    const response = await api.get(`/api/super-admin/billing/${tenantId}`);
    return response.data;
  },

  createSupportTicket: async (tenantId, ticket) => {
    const response = await api.post(`/api/super-admin/support/${tenantId}/tickets`, ticket);
    return response.data;
  },

  // Config Management
  getSystemConfig: async () => {
    const response = await api.get('/api/super-admin/config');
    return response.data;
  },

  updateSystemConfig: async (config) => {
    const response = await api.put('/api/super-admin/config', config);
    return response.data;
  },

  // Invite Links
  getInviteLinks: async (includeInactive = false) => {
    const response = await api.get(`/api/superadmin/invite-links?include_inactive=${includeInactive}`);
    return response.data;
  },

  createInviteLink: async (data) => {
    const response = await api.post('/api/superadmin/invite-links', data);
    return response.data;
  },

  getInviteLink: async (id) => {
    const response = await api.get(`/api/superadmin/invite-links/${id}`);
    return response.data;
  },

  updateInviteLink: async (id, data) => {
    const response = await api.put(`/api/superadmin/invite-links/${id}`, data);
    return response.data;
  },

  deleteInviteLink: async (id) => {
    const response = await api.delete(`/api/superadmin/invite-links/${id}`);
    return response.data;
  },

  validateInviteToken: async (token) => {
    const response = await api.get(`/api/superadmin/invite/validate/${token}`);
    return response.data;
  },
};

// Create a separate axios instance for tenant admin (uses different token)
const adminApi = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8004',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to tenant admin requests
adminApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('admin_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle token expiration and subscription enforcement for tenant admin
adminApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('admin_token');
      window.location.href = '/admin/login';
    }
    if (error.response?.status === 403 &&
        error.response?.data?.detail?.toLowerCase().includes('subscription')) {
      window.location.href = '/admin/billing?paywall=true';
    }
    return Promise.reject(error);
  }
);

// Public API (no auth required)
export const publicAPI = {
  validateInviteToken: async (token) => {
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8004';
    const response = await fetch(`${apiUrl}/api/superadmin/invite/validate/${token}`);
    return response.json();
  },
};

// Tenant Admin Auth API (Cognito-based)
export const authAPI = {
  signIn: async (username, password) => {
    const response = await adminApi.post('/api/auth/signin', {
      username,
      password,
    });
    return response.data;
  },

  signUp: async (username, email, password, plan = 'starter') => {
    const response = await adminApi.post('/api/auth/signup', {
      username,
      email,
      password,
      plan,
    });
    return response.data;
  },

  confirmSignUp: async (username, confirmation_code) => {
    const response = await adminApi.post('/api/auth/confirm-signup', {
      username,
      confirmation_code,
    });
    return response.data;
  },

  forgotPassword: async (username) => {
    const response = await adminApi.post('/api/auth/forgot-password', {
      username,
    });
    return response.data;
  },

  resetPassword: async (username, confirmation_code, new_password) => {
    const response = await adminApi.post('/api/auth/reset-password', {
      username,
      confirmation_code,
      new_password,
    });
    return response.data;
  },

  verify: async () => {
    const response = await adminApi.get('/api/auth/verify');
    return response.data;
  },
};

// Tenant Config API
export const configAPI = {
  getConfig: async () => {
    const response = await adminApi.get('/api/config');
    return response.data;
  },
};

// Documents API
export const documentsAPI = {
  getDocuments: async () => {
    const response = await adminApi.get('/api/documents/');
    return response.data;
  },

  uploadDocument: async (file, extractStructured = false) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('extract_structured', extractStructured);

    const response = await adminApi.post('/api/documents/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  deleteDocument: async (documentId) => {
    const response = await adminApi.delete(`/api/documents/${documentId}`);
    return response.data;
  },

  getDocument: async (documentId) => {
    const response = await adminApi.get(`/api/documents/${documentId}`);
    return response.data;
  },

  ingestUrl: async (url) => {
    const response = await adminApi.post('/api/documents/ingest-url', { url });
    return response.data;
  },

  refreshDocument: async (documentId) => {
    const response = await adminApi.post(`/api/documents/${documentId}/refresh`);
    return response.data;
  },

  getOutdatedDocuments: async () => {
    const response = await adminApi.get('/api/documents/outdated');
    return response.data;
  },
};

// Chat API
export const chatAPI = {
  sendMessage: async (message, sessionId = null) => {
    const response = await adminApi.post('/api/chat/query', {
      message,
      session_id: sessionId,
    });
    return response.data;
  },

  getChatSessions: async () => {
    const response = await adminApi.get('/api/chat/sessions');
    return response.data;
  },

  getChatSession: async (sessionId) => {
    const response = await adminApi.get(`/api/chat/sessions/${sessionId}`);
    return response.data;
  },
};

// Widgets API
export const widgetsAPI = {
  getWidgetConfig: async () => {
    const response = await adminApi.get('/api/widgets/config');
    return response.data;
  },

  updateWidgetConfig: async (config) => {
    const response = await adminApi.put('/api/widgets/config', config);
    return response.data;
  },
};

// Customer Profile API
export const customerAPI = {
  getProfile: async () => {
    const response = await adminApi.get('/api/customer/profile');
    return response.data;
  },

  updateProfile: async (profile) => {
    const response = await adminApi.put('/api/customer/profile', profile);
    return response.data;
  },

  getDashboard: async () => {
    const response = await adminApi.get('/api/customer/dashboard');
    return response.data;
  },

  uploadAvatar: async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await adminApi.post('/api/customer/avatar/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
};

// Agent Config API
export const agentAPI = {
  getConfig: async () => {
    const response = await adminApi.get('/api/agent/config');
    return response.data;
  },

  updateConfig: async (config) => {
    const response = await adminApi.put('/api/agent/config', config);
    return response.data;
  },

  previewPrompt: async (settings) => {
    const response = await adminApi.post('/api/agent/preview-prompt', settings);
    return response.data;
  },

  getPersonalityOptions: async () => {
    const response = await adminApi.get('/api/agent/personality-options');
    return response.data;
  },
};

// Billing API
export const billingAPI = {
  initializeTrial: async (plan, email, companyName = null, inviteToken = null) => {
    const response = await adminApi.post('/api/billing/initialize-trial', {
      plan,
      email,
      company_name: companyName,
      invite_token: inviteToken,
    });
    return response.data;
  },

  createSignupCheckout: async (params) => {
    const response = await adminApi.post('/api/billing/signup-checkout', params);
    return response.data;
  },

  getSubscription: async () => {
    const response = await adminApi.get('/api/billing/subscription');
    return response.data;
  },

  getUsage: async () => {
    const response = await adminApi.get('/api/billing/usage');
    return response.data;
  },

  getInvoices: async (limit = 20) => {
    const response = await adminApi.get(`/api/billing/invoices?limit=${limit}`);
    return response.data;
  },

  createCheckout: async (params) => {
    const response = await adminApi.post('/api/billing/checkout', params);
    return response.data;
  },

  createPortal: async (returnUrl) => {
    const response = await adminApi.post('/api/billing/portal', { return_url: returnUrl });
    return response.data;
  },

  cancelSubscription: async () => {
    const response = await adminApi.post('/api/billing/subscription/cancel');
    return response.data;
  },

  reactivateSubscription: async () => {
    const response = await adminApi.post('/api/billing/subscription/reactivate');
    return response.data;
  },

  getNotifications: async () => {
    const response = await adminApi.get('/api/billing/notifications');
    return response.data;
  },

  createUpgradeCheckout: async (params) => {
    const response = await adminApi.post('/api/billing/upgrade-checkout', params);
    return response.data;
  },
};

export default api;